import { fmtUSD0 } from "./dealMath";

/** Aggregate pipeline summary across all deals — gives the sponsor a
 *  single-glance view: total raise · committed · viewers · open count.
 *  Mirrors a CRM pipeline board's KPI strip without the column-by-column
 *  view (we have card grouping below). */
export default function PipelineSummary({ items }) {
  const totals = items.reduce((acc, d) => {
    const s = d.stats || {};
    acc.total += 1;
    if (d.status === "open") acc.open += 1;
    acc.equity_target += Number(d.equity_required) || 0;
    acc.committed += Number(s.committed_amount) || 0;
    acc.invited += s.invited || 0;
    acc.viewed += s.viewed || 0;
    acc.interested += s.interested || 0;
    return acc;
  }, { total: 0, open: 0, equity_target: 0, committed: 0, invited: 0, viewed: 0, interested: 0 });

  const pctRaised = totals.equity_target > 0
    ? Math.round((totals.committed / totals.equity_target) * 100)
    : 0;

  return (
    <div className="rounded-sm border border-slate-200 bg-white p-4 md:p-5" data-testid="deals-pipeline-summary">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <PipelineKpi
          label="Active Deals"
          value={`${totals.open}`}
          sub={`${totals.total} total`}
          tone="slate"
        />
        <PipelineKpi
          label="Equity Target"
          value={fmtUSD0(totals.equity_target)}
          sub="across pipeline"
          tone="slate"
        />
        <PipelineKpi
          label="Committed"
          value={fmtUSD0(totals.committed)}
          sub={`${pctRaised}% of target`}
          tone="indigo"
        />
        <PipelineKpi
          label="Investor Views"
          value={`${totals.viewed} / ${totals.invited}`}
          sub="viewed / invited"
          tone="slate"
        />
        <PipelineKpi
          label="Soft-Circled"
          value={`${totals.interested}`}
          sub="interested LPs"
          tone="emerald"
        />
      </div>
      {totals.equity_target > 0 && (
        <div className="mt-4">
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full transition-all ${pctRaised >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`}
              style={{ width: `${Math.min(pctRaised, 100)}%` }} />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
            <span>{fmtUSD0(totals.committed)} raised</span>
            <span>{fmtUSD0(totals.equity_target)} target</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PipelineKpi({ label, value, sub, tone }) {
  const valColor = {
    indigo: "text-indigo-700",
    emerald: "text-emerald-700",
    slate: "text-slate-900",
  }[tone] || "text-slate-900";
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 text-xl font-semibold font-mono ${valColor}`}>{value}</div>
      <div className="text-[10px] font-mono text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}
